import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  axios,
  StripeVerification,
  StripeVerificationService,
} from 'us-web-services';

import { useDependencies } from '../../dependencies';

type Action = 'start' | 'continue' | 'upload';

export const CompleteIdentityVerification = observer((): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const action = (searchParams.get('action') ?? 'start') as Action;

  const [status, setStatus] = useState<StripeVerification | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Identity Verification';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    (async (): Promise<void> => {
      try {
        // First get the current verification record (if any).
        const listResp = (
          await StripeVerificationService.getByFilter({
            params: { userId: user.id },
          })
        ).data;
        const existing = (listResp?.data ?? [])[0] ?? null;
        if (cancelled) return;

        let response: StripeVerification | null = null;
        if (action === 'continue' && existing?.id) {
          const resp = (
            await StripeVerificationService.patch(existing.id, {
              id: existing.id,
              userId: user.id,
            } as never)
          ).data;
          response = resp?.data ?? null;
        } else if (action === 'upload' && existing?.id) {
          const resp = (
            await StripeVerificationService.patch(existing.id, {
              id: existing.id,
              userId: user.id,
              type: 'document',
            } as never)
          ).data;
          response = resp?.data ?? null;
        } else {
          const resp = (
            await StripeVerificationService.create({
              userId: user.id,
            } as never)
          ).data;
          response = resp?.data ?? null;
        }

        if (!cancelled) {
          setStatus(response);
          if (response?.url) {
            // Auto-redirect to Stripe, matching Angular behavior.
            window.location.href = response.url;
          }
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
          toast.error(e.response.data.errors[0].message);
        } else {
          toast.error(t('pages.completeIdentityVerification.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, action, t]);

  if (loading) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('pages.completeIdentityVerification.loading')}</p>
      </div>
    );
  }

  return (
    <div className='container py-8 max-w-xl mx-auto'>
      {status?.url ? (
        <>
          <a
            id='continue-to-stripe'
            href={status.url}
            className='inline-block bg-sapphire text-white px-6 py-3 rounded font-semibold mb-4'
          >
            {t('pages.completeIdentityVerification.continueToStripe')}
          </a>
          <p className='text-sm text-neutral-700'>
            {t('pages.completeIdentityVerification.fallback')}
          </p>
        </>
      ) : (
        <p className='text-neutral-700'>{t('pages.completeIdentityVerification.expired')}</p>
      )}
    </div>
  );
});
